import { Menu, MenuItem, MenuItemLabel, MenuItemLeading, MenuItemTrailing } from '@plato/ui/primitives/menu';
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon, type LucideProps } from 'lucide-react';
import type { ComponentType } from 'react';

export const menuSeparatorClassName = 'my-cozy h-px w-full bg-neutral-200';

export type MenuBackRowProps = {
    label: string;
    onBack: () => void;
};

/**
 * Header of a pushed level: a full-width row that returns to the level above.
 * The whole row is the target rather than just the chevron — it is the only
 * exit from a pushed level, so it should be hard to miss.
 */
export function MenuBackRow({ label, onBack }: MenuBackRowProps) {
    return (
        <MenuItem label={label} onClick={onBack} className="cursor-pointer font-medium">
            <MenuItemLeading>
                <ChevronLeftIcon />
            </MenuItemLeading>
            <MenuItemLabel />
        </MenuItem>
    );
}

export type MenuDrillRowProps = {
    icon: ComponentType<LucideProps>;
    label: string;
    /** The current value, shown right-aligned so the row reads "label: value". */
    value: string;
    onOpen: () => void;
};

/** A setting collapsed to one row: what it is, its current value, and a way in. */
export function MenuDrillRow({ icon: Icon, label, value, onOpen }: MenuDrillRowProps) {
    return (
        <MenuItem
            // The row's accessible name combines label + value ("Language
            // English"), matching what a sighted user reads from the row —
            // MenuItemLabel's own children keep the visible text split.
            label={`${label} ${value}`}
            onClick={onOpen}
            className="cursor-pointer"
        >
            <MenuItemLeading>
                <Icon />
            </MenuItemLeading>
            <MenuItemLabel>
                <span className="truncate">{label}</span>
            </MenuItemLabel>
            <MenuItemTrailing>
                <span>{value}</span>
                <ChevronRightIcon />
            </MenuItemTrailing>
        </MenuItem>
    );
}

export type MenuOptionRowProps = {
    label: string;
    selected: boolean;
    onSelect: () => void;
};

/** One choice inside a pushed level, check-marked when it is the current one. */
export function MenuOptionRow({ label, selected, onSelect }: MenuOptionRowProps) {
    return (
        <MenuItem
            label={label}
            aria-current={selected ? 'true' : undefined}
            onClick={onSelect}
            className="cursor-pointer"
        >
            <MenuItemLabel />
            {selected && (
                <MenuItemTrailing>
                    {/* A lone selection check next to plain text needs both brand color and a
                    heavier stroke to read as active (the global .lucide rule pins 1.5). */}
                    <CheckIcon className="stroke-[2.5] text-brand" />
                </MenuItemTrailing>
            )}
        </MenuItem>
    );
}

export type MenuActionRowProps = {
    icon: ComponentType<LucideProps>;
    label: string;
    onSelect: () => void;
};

/** A row that runs an action rather than opening a level, so it has no chevron. */
export function MenuActionRow({ icon: Icon, label, onSelect }: MenuActionRowProps) {
    return (
        <MenuItem label={label} onClick={onSelect} className="cursor-pointer">
            <MenuItemLeading>
                <Icon />
            </MenuItemLeading>
            <MenuItemLabel>
                <span className="truncate">{label}</span>
            </MenuItemLabel>
        </MenuItem>
    );
}

export { Menu };
